import { QueryFunction, useQuery } from '@tanstack/react-query';
import { BasicMarkets, createBasicMarket } from 'app/entities/BasicMarket';
import { flow, pipe } from 'fp-ts/lib/function';
import * as Either from 'fp-ts/Either';
import { partial } from 'lodash/fp';
import staticMainnetLegacyMarkets from '../../staticmarkets/mainnetLegacy.json';
import staticMainnetLlamaMarkets from '../../staticmarkets/mainnetLlama.json';
import staticMainnetGrandMarkets from '../../staticmarkets/mainnetGrand.json';
import staticLocalMarkets from '../../staticmarkets/local.json';
import staticArbitrumMarkets from '../../staticmarkets/arbitrum.json';
import staticOptimismMarkets from '../../staticmarkets/optimism.json';
import staticBaseMarkets from '../../staticmarkets/base.json';
import staticLrtbaseMarkets from '../../staticmarkets/lrtbase.json';
import staticAnvilMarkets from '../../staticmarkets/anvil.json';
import { MarketsError } from './types';
import { getOrThrow } from 'app/features/util/either';
import { useSiloWeb3 } from '@silo/web3';
import { GetConfigurationForSilo, useSiloConfiguration } from '@silo/config-legacy';
import { isNotNullNorUndefined } from '@silo/utils';
import { ChecksumAddress, ProtocolInstance, ProtocolInstanceValue, RUNTIME_PROTOCOL_INSTANCES, StaticMarketsLegacy } from '@silo/types';
import produce from 'immer';

const allFallbackMarketsKey = 'allFallbackMarkets';

export type AllFallbackMarketsKey = [
  key: typeof allFallbackMarketsKey,
  protocolInstance: ProtocolInstance,
  getConfigurationForSilo: GetConfigurationForSilo
];

const fallbackMarketsFromStaticMarkets = ({
  protocolInstance,
  getConfigurationForSilo,
}: {
  protocolInstance: ProtocolInstance;
  getConfigurationForSilo: GetConfigurationForSilo;
}) => {
  const result = getStaticMarkets(protocolInstance, getConfigurationForSilo);
  const staticMarkets = getOrThrow(result);

  return staticMarkets.map(createBasicMarket.fromStaticMarket(protocolInstance));
};

const queryAllFallbackMarkets: QueryFunction<BasicMarkets, AllFallbackMarketsKey> = async ({
  queryKey: [_, protocolInstance, getConfigurationForSilo],
}) => {
  return fallbackMarketsFromStaticMarkets({
    protocolInstance,
    getConfigurationForSilo,
  });
};

/**
 * Previously this hook filtered the static market list through an on-chain
 * readiness check (`useAreSilosReady`). On the deprecated app that sweep is
 * pure RPC waste — the static market JSON for live protocol instances only
 * contains deployed silos, and `useValidateSiloAddress` already does an
 * on-chain validity check for the URL-provided silo on the silo page. This is
 * now a passthrough to the unfiltered static list; the `enabled` param is
 * preserved for call-site compatibility but ignored.
 */
export const useQueryReadyFallbackMarkets = (_enabled?: boolean) => {
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);

  const { data, error } = useQuery<BasicMarkets, MarketsError, BasicMarkets, AllFallbackMarketsKey>({
    queryKey: [allFallbackMarketsKey, protocolInstance!, getConfigurationForSilo],
    queryFn: queryAllFallbackMarkets,
    enabled: isNotNullNorUndefined(protocolInstance),
  });

  return { data, error: error ?? undefined };
};

export type StaticMarketsMap = ProtocolInstanceValue<any>;

const STATIC_MARKETS_MAP: StaticMarketsMap = {
  mainnetLegacy: staticMainnetLegacyMarkets,
  mainnetLlama: staticMainnetLlamaMarkets,
  mainnetGrand: staticMainnetGrandMarkets,
  arbitrum: staticArbitrumMarkets,
  optimism: staticOptimismMarkets,
  base: staticBaseMarkets,
  lrtbase: staticLrtbaseMarkets,
  anvil: staticAnvilMarkets,
  local: staticLocalMarkets,
};

export const baseGetStaticMarkets = (
  STATIC_MARKETS_MAP: StaticMarketsMap,
  protocolInstance: ProtocolInstance | undefined,
  getConfigurationForSilo: GetConfigurationForSilo
) =>
  pipe(
    protocolInstance,
    Either.fromNullable({ _tag: 'MissingProtocolInstanceError', protocolInstance } as MarketsError),
    Either.map((protocolInstance) => STATIC_MARKETS_MAP[protocolInstance]),
    Either.chain(
      flow(
        StaticMarketsLegacy.decode,
        Either.mapLeft((errors): MarketsError => ({ _tag: 'DecodeError', protocolInstance, error: errors }))
      )
    ),
    Either.map((staticMarkets) =>
      produce(staticMarkets, (draft) => {
        for (const { marketAssets, id: marketId } of draft) {
          const [base] = marketAssets;
          const { name, symbol } = base.inputToken;
          const config = getConfigurationForSilo(marketId);
          base.inputToken.name = config.name_MUST_SYNCHRONIZE ?? name;
          base.inputToken.symbol = config.symbol_MUST_SYNCHRONIZE ?? symbol;
        }
      })
    )
  );

export const getStaticMarkets = partial(baseGetStaticMarkets, [STATIC_MARKETS_MAP]);

/**
 * Synchronously scan every protocol's static markets JSON for a market id
 * matching `address`. Used to replace the legacy `useValidateSiloAddress`
 * on-chain probe — the static blob is the source of truth for deployed silos,
 * so this returns the same answer with zero RPC.
 *
 * Note: `STATIC_MARKETS_MAP` is typed `any` (see `StaticMarketsMap`), so the
 * defensive parse on `market.id` is intentional.
 */
export function findProtocolInstanceForMarket(address: ChecksumAddress): ProtocolInstance | null {
  const normalized = address.toLowerCase();

  for (const protocolInstance of RUNTIME_PROTOCOL_INSTANCES) {
    const markets = STATIC_MARKETS_MAP[protocolInstance];
    if (!Array.isArray(markets)) continue;

    for (const market of markets) {
      const id = (market as { id?: unknown }).id;
      if (typeof id === 'string' && id.toLowerCase() === normalized) {
        return protocolInstance;
      }
    }
  }

  return null;
}
